import React from 'react';
import Icon from '../../../components/AppIcon';

const CompletionStatus = ({ hasAudio, hasGlossary, isReviewed, lineData }) => {
  const validationChecks = [
    {
      id: 'audio',
      label: 'Audio Recording',
      status: hasAudio,
      icon: 'Music',
      description: hasAudio ? 'Audio file uploaded' : 'No audio file uploaded'
    },
    {
      id: 'glossary',
      label: 'Glossary Terms',
      status: hasGlossary,
      icon: 'BookText',
      description: hasGlossary 
        ? `${lineData?.assignedGlossaryTerms?.length || 0} term(s) assigned` 
        : 'No glossary terms assigned'
    },
    {
      id: 'character',
      label: 'Character Assigned',
      status: !!lineData?.character,
      icon: 'User',
      description: lineData?.character || 'No character assigned'
    },
    {
      id: 'dialogue',
      label: 'Dialogue Text',
      status: !!lineData?.text && lineData?.text?.trim()?.length > 0,
      icon: 'MessageSquare',
      description: lineData?.text?.trim()?.length > 0 
        ? `${lineData?.text?.split(' ')?.length} words` 
        : 'No dialogue text'
    }
  ];

  const completedChecks = validationChecks?.filter(check => check?.status)?.length;
  const totalChecks = validationChecks?.length;
  const completionPercentage = (completedChecks / totalChecks) * 100;

  const getStatusColor = () => {
    if (completionPercentage === 100) return 'text-success';
    if (completionPercentage >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const getStatusIcon = () => {
    if (completionPercentage === 100) return 'CheckCircle2';
    if (completionPercentage >= 50) return 'AlertCircle';
    return 'XCircle';
  };

  const getStatusLabel = () => {
    if (completionPercentage === 100) return 'Complete';
    if (completionPercentage >= 50) return 'In Progress';
    return 'Incomplete';
  };

  return (
    <div className="p-6 bg-background border-t border-border">
      <div className="flex items-start gap-3 mb-4">
        <Icon name={getStatusIcon()} size={20} className={`${getStatusColor()} mt-1`} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-foreground">
              Line Completion Status
            </h3>
            <span className={`text-sm font-bold ${getStatusColor()}`}>
              {completedChecks}/{totalChecks} {getStatusLabel()}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Validation indicators for content review workflow
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              completionPercentage === 100
                ? 'bg-success'
                : completionPercentage >= 50
                ? 'bg-warning' :'bg-destructive'
            }`}
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Validation Checks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {validationChecks?.map((check) => (
          <div
            key={check?.id}
            className={`bg-card border rounded-lg p-3 transition-all ${
              check?.status
                ? 'border-success/30 bg-success/5' :'border-border hover:border-muted-foreground/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  check?.status
                    ? 'bg-success/20 text-success' :'bg-muted text-muted-foreground'
                }`}
              >
                {check?.status ? (
                  <Icon name="Check" size={16} />
                ) : (
                  <Icon name={check?.icon} size={16} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">
                    {check?.label}
                  </p>
                  {check?.status && (
                    <Icon name="CheckCircle2" size={14} className="text-success" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {check?.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Review Status */}
      {completionPercentage === 100 && (
        <div className="mt-4 p-3 bg-success/10 border border-success/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle2" size={16} className="text-success" />
            <p className="text-sm font-medium text-success">
              All validation checks passed! Line is ready for review.
            </p>
          </div>
        </div>
      )}

      {completionPercentage < 100 && (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warning mb-1">
                Incomplete Line Data
              </p>
              <p className="text-xs text-muted-foreground">
                Complete all validation checks to improve editing efficiency and reduce modal switching.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletionStatus;